import { inject, observer } from "mobx-react";
import {
  Badge,
  Button,
  Label,
  ProductOverviewPage,
  Skeleton,
  Switch,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  useChat,
} from "randmarcomps";
import * as React from "react";
import { Link, useParams } from "react-router-dom";
import { AlertTriangle, CheckCircle2, EyeOff, Info, OctagonX, PauseCircle, RefreshCw, Sparkles } from "lucide-react";
import MarginsGrid from "../components/grids/MarginsGrid";
import EditDistributionInformation from "../components/products/EditDistributionInformation";
import EditShopifyListing from "../components/products/EditShopifyListing";
import InstantRebatesGrid from "../components/products/InstantRebatesGrid";
import ProductActions from "../components/products/ProductActions";
import OpenOrdersSummary from "../componentsCore/cards/OpenOrdersSummary";
import withManufacturer from "../contexts/WithManufacturer";
import Path from "../routes/Path";
import { RootStore } from "../stores/rootStore";
import withRoot from "../withRoot";

function buildPublicListingRefreshPrompt(randmarSKU: string) {
  return [
    `Get Product ${randmarSKU}`,
    "",
    "Using Google Search and reputable online sources, propose improved Shopify-ready copy for:",
    "- BodyHTML (highest priority)",
    "- Title",
    "- VoiceoverCaption",
    "",
    "Requirements:",
    "- Share 2–5 sources (URLs) you used.",
    "- Provide your recommended new values in a clearly labeled format.",
    "- Do not apply any changes until I confirm each field (BodyHTML, Title, VoiceoverCaption).",
    "- Keep BodyHTML clean, semantic, and safe (no scripts; ok to use headings, paragraphs, and bullet lists).",
  ].join("\n");
}

interface IProps {
  store?: RootStore;
  applicationId: string;
}

const Product: React.FC<IProps> = (props) => {
  const { randmarSKU } = useParams<{ randmarSKU: string }>();
  const [loading, setLoading] = React.useState(true);
  const [autoUpdateBusy, setAutoUpdateBusy] = React.useState(false);
  const [shopifyListingRefreshKey, setShopifyListingRefreshKey] = React.useState(0);
  const heroHeadingId = React.useId();
  const autoUpdateSwitchId = React.useId();
  const autoUpdateDescriptionId = React.useId();
  const { sendMessageWithClear } = useChat();
  const { applicationId, store } = props;
  const { productStore, snackStore } = store!;

  React.useEffect(() => {
    let isMounted = true;

    const fetchProduct = async () => {
      setLoading(true);
      await productStore.getProduct(applicationId, randmarSKU || "");
      if (isMounted) {
        setLoading(false);
      }
    };

    fetchProduct();

    return () => {
      isMounted = false;
    };
  }, [applicationId, randmarSKU, productStore]);

  const updateProductProperty = React.useCallback(async (propName: string, newValue: string, options?: { onFinally?: () => void }) => {
    const sku = productStore.product?.randmarSKU || "";
    if (!sku) {
      options?.onFinally?.();
      return;
    }

    setLoading(true);
    try {
      const success = await productStore.setProductProperty(applicationId, sku, propName, newValue);
      snackStore.showResult(success);
      await productStore.getProduct(applicationId, sku);
    } finally {
      setLoading(false);
      options?.onFinally?.();
    }
  }, [applicationId, productStore, snackStore]);

  const handleAutoUpdateChange = React.useCallback(async (checked: boolean) => {
    setAutoUpdateBusy(true);
    await updateProductProperty("AutoUpdate", String(checked), { onFinally: () => setAutoUpdateBusy(false) });
  }, [updateProductProperty]);

  const { product } = productStore;
  const productSku = product?.randmarSKU ?? randmarSKU ?? "";
  const categoryCode = product?.category?.trim();

  const handleRefresh = React.useCallback(async () => {
    if (!productSku) return;
    setLoading(true);
    try {
      await productStore.getProduct(applicationId, productSku);
    } finally {
      setLoading(false);
    }
  }, [applicationId, productSku, productStore]);

  const handleShopifyListingRefresh = React.useCallback(async () => {
    await handleRefresh();
    setShopifyListingRefreshKey((current) => current + 1);
  }, [handleRefresh]);

  const stateBadge = React.useMemo((): { variant: React.ComponentProps<typeof Badge>["variant"]; icon: React.ReactNode } | undefined => {
    const normalizedState = product?.state?.toLowerCase();
    if (!normalizedState) return undefined;
    if (normalizedState === "active") return { variant: "success", icon: <CheckCircle2 className="h-4 w-4" aria-hidden="true" /> };
    if (normalizedState === "discontinued") return { variant: "destructive", icon: <OctagonX className="h-4 w-4" aria-hidden="true" /> };
    if (normalizedState === "hold") return { variant: "warning", icon: <PauseCircle className="h-4 w-4" aria-hidden="true" /> };
    return { variant: "info", icon: <Info className="h-4 w-4" aria-hidden="true" /> };
  }, [product?.state]);

  const pageContainerClassName = "mx-auto w-full max-w-[1440px] space-y-8 pb-14";
  const surfaceClassName = "rounded-2xl border border-gray-100 bg-white p-6 shadow-sm focus-within:outline-none focus-within:ring-2 focus-within:ring-[var(--ops-focus-ring)]";
  const heroSurfaceClassName = "relative overflow-hidden rounded-2xl border border-gray-100 bg-[var(--assistant-prompt-gradient)] p-6 shadow-sm";

  if (!product) {
    return (
      <div className={pageContainerClassName} role="status" aria-live="polite">
        <div className="h-1 w-full overflow-hidden rounded-full bg-slate-200" aria-atomic="true">
          <span className="sr-only">Loading product</span>
          <div className="h-full w-1/2 animate-pulse bg-[var(--ops-accent)] transition-all duration-700 ease-out" />
        </div>

        <header className={heroSurfaceClassName}>
          <div className="space-y-4">
            <Skeleton className="h-7 w-1/3" />
            <Skeleton className="h-10 w-3/4" />
            <Skeleton className="h-5 w-1/2" />
            <div className="flex flex-wrap gap-3 pt-2">
              <Skeleton className="h-7 w-28 rounded-full" />
              <Skeleton className="h-7 w-32 rounded-full" />
              <Skeleton className="h-7 w-36 rounded-full" />
            </div>
          </div>
        </header>

        <section className={surfaceClassName} aria-label="Buyer preview">
          <Skeleton className="h-96 w-full rounded-2xl" />
        </section>
      </div>
    );
  }

  const autopilotStatus = autoUpdateBusy
    ? "Saving automatic reorder preference…"
    : product.autoUpdate
      ? "Inventory sync is keeping buyers up to date."
      : "Manual updates required to keep listings fresh.";

  return (
    <div className={pageContainerClassName} aria-busy={loading}>
      <div className="h-1 w-full overflow-hidden rounded-full bg-slate-200" role="status" aria-live="polite" aria-atomic="true">
        <span className="sr-only">{loading ? "Loading product details" : "Product details loaded"}</span>
        <div
          className={`h-full bg-[var(--ops-accent)] transition-all duration-700 ease-out ${loading ? "w-1/2 animate-pulse" : "w-full"}`}
        />
      </div>

      <header className={heroSurfaceClassName} role="banner" aria-labelledby={heroHeadingId}>
        <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-4">
            <div className="space-y-1">
              <h1 id={heroHeadingId} className="text-[32px] font-semibold leading-tight text-slate-900">
                {product.title || product.mpn || "Product"}
              </h1>
              {product.mpn && (
                <p className="text-[13px] text-slate-500" aria-label="Manufacturer part number">
                  {product.mpn}
                </p>
              )}
            </div>

            <div className="flex flex-wrap gap-2" role="list" aria-label="Product status">
              {product.state && stateBadge && (
                <Badge role="listitem" variant={stateBadge.variant} size="sm" icon={stateBadge.icon}>
                  {product.state}
                </Badge>
              )}
              {product.opportunityOnly && (
                <Badge role="listitem" variant="warning" size="sm" icon={<AlertTriangle className="h-4 w-4" aria-hidden="true" />}>
                  Opportunity only
                </Badge>
              )}
              {product.availableToBuy === false && (
                <Badge role="listitem" variant="secondary" size="sm" icon={<EyeOff className="h-4 w-4" aria-hidden="true" />}>
                  Hidden from catalog
                </Badge>
              )}
            </div>

            <p className="text-[15px] text-slate-600">
              {product.manufacturerName ? `${product.manufacturerName} • ` : ""}
              {categoryCode ? (
                <>
                  <Link
                    to={Path.category(applicationId, categoryCode)}
                    className="font-medium text-slate-700 underline-offset-2 hover:text-slate-900 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ops-focus-ring)] focus-visible:ring-offset-2"
                    aria-label={`View category ${product.productType || categoryCode}`}
                  >
                    {product.productType || categoryCode}
                  </Link>{" "}
                  <span className="text-slate-500">· {categoryCode}</span>
                </>
              ) : (
                <>
                  {product.productType || "Product"} <span className="text-slate-500">· Uncategorized</span>
                </>
              )}
            </p>
          </div>

          <div className="flex w-full flex-col gap-4 lg:max-w-md lg:items-end">
            {product.isManufacturer && (
              <div
                className="w-full rounded-2xl border border-gray-100 bg-white/80 p-4 shadow-sm"
                data-state={product.autoUpdate ? "on" : "off"}
                data-busy={autoUpdateBusy}
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="space-y-1">
                    <Label htmlFor={autoUpdateSwitchId} className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                      Automatic reorder
                    </Label>
                    <p id={autoUpdateDescriptionId} aria-live="polite" className="text-[13px] text-slate-500">
                      {autopilotStatus}
                    </p>
                  </div>
                  <Switch
                    id={autoUpdateSwitchId}
                    aria-describedby={autoUpdateDescriptionId}
                    checked={Boolean(product.autoUpdate)}
                    onCheckedChange={handleAutoUpdateChange}
                    disabled={autoUpdateBusy}
                  />
                </div>
              </div>
            )}

            <div className="flex w-full flex-wrap justify-end gap-2" role="group" aria-label="Product actions">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className="h-10 gap-2 rounded-xl"
                      onClick={handleRefresh}
                      disabled={!productSku || loading}
                    >
                      <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} aria-hidden="true" />
                      Refresh
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs text-sm">Reloads the latest product details.</TooltipContent>
                </Tooltip>
              </TooltipProvider>
              {product.isManufacturer && <ProductActions product={product} />}
            </div>
          </div>
        </div>
      </header>

      <section role="region" aria-label="Open orders insights">
        <OpenOrdersSummary openOrders={product.activeOrderDetails} />
      </section>

      <section className={surfaceClassName} aria-label="Experience preview">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Buyer preview</p>
          <h2 className="mt-1 text-[24px] font-semibold text-slate-900">Product overview</h2>
        </div>
        <div className="mt-6">
          <ProductOverviewPage applicationId={applicationId} sku={product.randmarSKU ?? ""} />
        </div>
      </section>

      {product.isManufacturer && product.manufacturerCategory && (
        <section className="space-y-4" aria-label="Margin guardrails">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Profitability</p>
            <h2 className="mt-1 text-[24px] font-semibold text-slate-900">Margins & packages</h2>
          </div>
          <MarginsGrid category={product.manufacturerCategory} distribution={product.distribution} />
        </section>
      )}

      {product.isManufacturer && (
        <section className="space-y-4" aria-label="Distribution details">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Availability</p>
            <h2 className="mt-1 text-[24px] font-semibold text-slate-900">Distribution information</h2>
          </div>
          <EditDistributionInformation product={product} />
        </section>
      )}

      {product.instantRebates && product.instantRebates.length > 0 && (
        <section className="space-y-4" aria-label="Instant rebates">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Incentives</p>
            <h2 className="mt-1 text-[24px] font-semibold text-slate-900">Instant rebates</h2>
          </div>
          <InstantRebatesGrid instantRebates={product.instantRebates} />
        </section>
      )}

      {product.isManufacturer && (
        <section className={surfaceClassName} aria-label="Public listing configuration">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Shopify</p>
              <h2 className="mt-1 text-[24px] font-semibold text-slate-900">Public listing</h2>
            </div>
            <TooltipProvider>
              <div className="flex w-full items-center gap-2 md:w-auto">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-10 flex-1 gap-2 rounded-xl md:flex-none"
                      disabled={!productSku}
                      onClick={() => sendMessageWithClear(buildPublicListingRefreshPrompt(productSku))}
                    >
                      <Sparkles className="h-4 w-4" aria-hidden="true" />
                      Refresh Copy
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs text-sm">
                    Prompts Randmar Assistant to suggest updated Title, Voiceover caption, and HTML Description (you confirm before
                    applying).
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-10 w-10 rounded-xl"
                      onClick={handleShopifyListingRefresh}
                      disabled={!productSku || loading}
                      aria-label="Refresh public listing data"
                    >
                      <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} aria-hidden="true" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs text-sm">Refreshes the latest listing data from Randmar.</TooltipContent>
                </Tooltip>
              </div>
            </TooltipProvider>
          </div>
          <div className="mt-6">
            <EditShopifyListing key={`shopifyListing_${productSku}_${shopifyListingRefreshKey}`} product={product} applicationId={applicationId} />
          </div>
        </section>
      )}
    </div>
  );
};

export default withRoot(withManufacturer(inject("store")(observer(Product))));
